import { InformationCircleIcon, PlusCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import pick from 'lodash/pick'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { v4 as uuid } from 'uuid'

import type { Part } from './useForm'

import { DeliveryMethod, RfqCreateQuoteQuery, useCreateQuoteMutation } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useMsgs from '@/gf/hooks/useMsgs'
import MoneyM from '@/gf/modules/Money'
import useUpload from '../../CreateExternalOrder/useUpload'
import useForm from './useForm'

import DocumentUpload from '@/buyers/components/DocumentUpload'
import Action from '@/gf/components/Action'
import AddressInput from '@/gf/components/AddressInput'
import Card from '@/gf/components/Card'
import Field from '@/gf/components/Field'
import Form from '@/gf/components/Form'
import InStockCheckbox from '@/gf/components/InStockCheckbox'
import Layout from '@/gf/components/Layout'
import TextInput from '@/gf/components/TextInput'
import DateInput from '@/gf/components/inputs/DateNext'
import PriceInput from '@/gf/components/inputs/PriceV2'
import QuantityInput from '@/gf/components/inputs/Quantity'
import PricesForm from '../PricesForm'
import VendorCard from './VendorCard'

type Rfq = RfqCreateQuoteQuery['rfqs'][number]
type VendorLink = RfqCreateQuoteQuery['rfqs'][number]['vendorLinks'][number]
type Vendor = VendorLink['vendor']

const zeroPrice = MoneyM.fromDecimal(0, 'USD')

// Vendors write the same part number a dozen ways — "1R-0750", "1r0750", "1R 0750".
// Compare on letters and digits only so an extracted line still lands on the part the
// buyer requested, which is what keeps the quote line tied to the request.
const partKey = (mpn: string) => mpn.replace(/[^a-z0-9]/gi, '').toUpperCase()

const CreateQuoteForm = ({
  rfq,
  vendor,
  vendorLink,
  windowIsMd,
}: {
  rfq: Rfq
  vendor: Vendor
  vendorLink: VendorLink
  windowIsMd: boolean
}) => {
  const navigate = useNavigate()
  const [_, msgr] = useMsgs()
  const [adding, setAdding] = useState(false)
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  // The uploaded object's own URL is what gets extracted and submitted; only the preview
  // reads through the authed proxy (GEA-4585). Null with the proxy off — render the object.
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string | null>(null)
  const client = useGqlClient()
  const [createQuote] = useCreateQuoteMutation({ client })

  const buildPart = (attrs?: Partial<Part>) => ({
    id: uuid(),
    mpn: '',
    rfqPartMpn: '',
    rfqPartId: null,
    name: '',
    description: '',
    quantity: 1,
    unitPrice: null,
    inStock: true,
    availableAt: null,
    externalId: null,
    ...(attrs || {}),
  })

  const { form, errors, updateForm, updateErrors, addPart, removePart, updatePart } = useForm({
    parts: [buildPart()],
    quoteNumber: '',
    shippingCost: zeroPrice,
    taxCost: zeroPrice,
    pickupAddress: {
      firstName: '',
      lastName: '',
      companyName: '',
      lineOne: '',
      lineTwo: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'United States',
    },
  })

  useEffect(() => {
    const pickupAddress = vendorLink?.pickupAddress || vendor.address || vendor.store?.address
    const pickupAddressUpdate = pickupAddress ? { pickupAddress } : {}

    const parts = rfq.parts.map((p) =>
      buildPart({
        mpn: p.mpn,
        rfqPartMpn: p.mpn,
        rfqPartId: p.id,
        name: p.description,
        // Use remainingQuantity when available (after partial inventory fulfillment)
        quantity: p.remainingQuantity ?? p.quantity,
        externalId: p.externalId,
      })
    )

    updateForm({ ...pickupAddressUpdate, parts })
  }, [])

  // The rows on this page start out as the parts the buyer requested, each carrying the
  // `rfqPartId` that ties the quote line back to the request. So an extracted line is
  // merged onto its matching row rather than replacing the list: prices land where they
  // belong, the link survives, and anything the vendor quoted that wasn't asked for is
  // appended as a new row (GEA-6418).
  const { uploadProcessing, uploadStarted, resetUpload, extractOrderDoc, documentId } = useUpload(
    (orderDoc) => {
      const extracted = (orderDoc.items || []).map(({ properties }) => {
        const unitPrice = properties.find((p) => p.__typename === 'OrderDocItemUnitPrice')?.price
        const totalPrice = properties.find((p) => p.__typename === 'OrderDocItemTotalPrice')?.price
        const quantity = properties.find((p) => p.__typename === 'OrderDocItemQuantity')?.quantity

        return {
          mpn: properties.find((p) => p.__typename === 'OrderDocItemPartNumber')?.text || '',
          name: properties.find((p) => p.__typename === 'OrderDocItemDescription')?.text || '',
          // Without a unit price the extracted price is a line total, so keep the
          // quantity at 1 rather than multiplying the total by it.
          unitPrice: unitPrice || totalPrice || null,
          quantity: (unitPrice && quantity) || 1,
        }
      })

      const unmatched = [...extracted]

      const merged = form.parts.map((part) => {
        if (!part.mpn) return part

        const index = unmatched.findIndex((e) => e.mpn && partKey(e.mpn) === partKey(part.mpn))
        if (index === -1) return part

        const [match] = unmatched.splice(index, 1)

        return {
          ...part,
          unitPrice: match.unitPrice || part.unitPrice,
          quantity: match.quantity || part.quantity,
        }
      })

      const parts = [...merged, ...unmatched.map((e) => buildPart(e))]
      const tax = orderDoc.properties.find((p) => p.__typename === 'OrderDocTax')?.price
      const shipping = orderDoc.properties.find(
        (p) => p.__typename === 'OrderDocShippingCost'
      )?.price

      updateForm({
        parts: parts.length > 0 ? parts : [buildPart()],
        taxCost: tax || form.taxCost,
        shippingCost: shipping || form.shippingCost,
      })
    },
    {
      // Scoped to this RFQ + vendor, not shared across the page. An extraction still
      // in flight is resumed from localStorage, so one key for every quote would let
      // RFQ A's upload resume on RFQ B — prefilling B with A's prices and attaching
      // A's PDF to B's quote. Wrong document on the record is worse than none.
      storageKey: `create-quote-doc-extraction-id:${rfq.id}:${vendor.id}`,
      notFoundMessage: 'No quote details were found in the file.',
    }
  )

  const onDocumentUploadComplete = (url: string, previewUrl: string | null) => {
    setDocumentUrl(url)
    setDocumentPreviewUrl(previewUrl)
    extractOrderDoc(url)
  }

  const clearDocument = () => {
    setDocumentUrl(null)
    setDocumentPreviewUrl(null)
    resetUpload()
  }

  const subtotal = form.parts
    .map((part) => MoneyM.mult(part.unitPrice || zeroPrice, part.quantity || 1))
    .reduce(MoneyM.add)

  const onSubmit = () => {
    // Validate parts
    const partErrors: Record<string, string | null>[] = form.parts.map((part) => ({
      mpn: part.mpn.trim() ? null : 'Part number is required',
      name: part.name.trim() ? null : 'Name is required',
      quantity: part.quantity && part.quantity >= 1 ? null : 'Quantity must be at least 1',
      unitPrice: part.unitPrice ? null : 'Unit price is required',
      availableAt: null,
    }))

    const hasPartErrors = partErrors.some((pe) => Object.values(pe).some((v) => v !== null))

    if (hasPartErrors) {
      updateErrors({
        details: {
          parts: partErrors.map((pe) => {
            const result: Record<string, string[]> = {}
            for (const [k, v] of Object.entries(pe)) {
              if (v) result[k] = [v]
            }
            return result
          }),
        },
      })
      msgr.add('Please fix the errors in the form.', 'negative')
      return
    }

    // Validate pickup address when pickup is enabled
    if (vendorLink?.pickup) {
      const addr = form.pickupAddress
      if (!addr.lineOne?.trim() || !addr.city?.trim() || !addr.state?.trim()) {
        msgr.add('Pickup address is required when pickup is selected.', 'negative')
        return
      }
    }

    // Submitting mid-extraction would send documentId: null and strand the file the
    // buyer just uploaded — the exact loss this page exists to stop (GEA-6418).
    if (uploadProcessing) {
      msgr.add('Still reading the uploaded quote. Give it a moment.', 'negative')
      return
    }

    setAdding(true)

    const parts = form.parts.map((part) => {
      const availableAt = !part.inStock && part.availableAt ? part.availableAt.toISODate() : null

      return {
        mpn: part.mpn,
        name: part.name,
        quantity: part.quantity,
        unitPrice: part.unitPrice,
        inStock: part.inStock,
        availableAt,
        rfqPartId: part.rfqPartId,
        externalId: part.externalId,
      }
    })

    createQuote({
      variables: {
        rfqId: rfq.id,
        vendorId: vendor.id,
        parts,
        quoteNumber: form.quoteNumber,
        shippingCost: form.shippingCost,
        taxCost: form.taxCost,
        pickupAddress: vendorLink?.pickup
          ? pick(form.pickupAddress, [
              'companyName',
              'firstName',
              'lastName',
              'lineOne',
              'lineTwo',
              'city',
              'state',
              'country',
              'postalCode',
            ])
          : null,
        documentId,
      },
    })
      .then(() => {
        msgr.add('Quote created.', 'positive')
        navigate(`/rfqs/${rfq.id}/request`)
      })
      .catch((err) => {
        const gqlError = err.graphQLErrors[0]

        if (gqlError) {
          updateErrors(gqlError)
          msgr.add(gqlError.message, 'negative')
        } else msgr.addUnknownError()
      })
      .finally(() => setAdding(false))
  }

  return (
    <Form onSubmit={onSubmit}>
      <Layout>
        {!windowIsMd && (
          <Layout.Section type="full">
            <VendorCard vendor={vendor} vertical={false} />
          </Layout.Section>
        )}

        <Layout.Section type={windowIsMd ? 'primary' : 'full'}>
          <Card>
            <Card.Section>
              <div className="flex flex-row gap-x-1 items-center text-sm text-gray-700">
                <InformationCircleIcon className="w-5 h-5 flex shrink-0" />
                Upload the vendor&apos;s quote to fill in the prices, or call your Vendor and enter
                them in the form below. Or enter your order information from an OEM portal.
              </div>
            </Card.Section>

            <Card.Section>
              <div className="space-y-8">
                <div className={form.parts.length > 1 ? 'divide-y' : ''}>
                  {form.parts.map((part, index) => (
                    <div
                      className="flex flex-wrap gap-8 justify-between items-center py-6 first:pt-0"
                      key={part.id}
                    >
                      <div className="flex flex-col gap-y-2 flex-wrap grow">
                        <div className="flex gap-x-4 gap-y-2 flex-wrap grow">
                          <div className="grow space-y-2">
                            <div className="flex gap-x-4 gap-y-2 flex-wrap grow">
                              <Field
                                label="Part Number"
                                errors={errors.parts[index]?.mpn}
                                htmlFor={`${part.id}-mpn`}
                              >
                                <div className="w-52">
                                  <TextInput
                                    value={part.mpn}
                                    setValue={(mpn) => updatePart(part.id, { mpn })}
                                    id={`${part.id}-mpn`}
                                  />
                                </div>
                              </Field>

                              <div className="grow">
                                <Field
                                  label="Name"
                                  errors={errors.parts[index]?.name}
                                  htmlFor={`${part.id}-name`}
                                >
                                  <div className="min-w-72">
                                    <TextInput
                                      value={part.name}
                                      setValue={(name) => updatePart(part.id, { name })}
                                      id={`${part.id}-name`}
                                    />
                                  </div>
                                </Field>
                              </div>
                            </div>

                            {part.rfqPartId && part.rfqPartMpn && part.mpn !== part.rfqPartMpn && (
                              <div className="flex px-2 py-1 bg-gray-100 border border-gray-300 rounded-md text-sm max-w-prose">
                                <div>
                                  Requested part:{' '}
                                  <span className="font-medium">{part.rfqPartMpn}</span>. If this is
                                  different from the requested part, remove it and add a new part.
                                </div>
                              </div>
                            )}
                          </div>

                          <Field
                            label="Quantity"
                            errors={errors.parts[index]?.quantity}
                            htmlFor={`${part.id}-quantity`}
                          >
                            <div className="w-24">
                              <QuantityInput
                                value={part.quantity}
                                setValue={(quantity) => updatePart(part.id, { quantity })}
                                min={1}
                                id={`${part.id}-quantity`}
                              />
                            </div>
                          </Field>

                          <Field
                            label="Unit Price"
                            errors={errors.parts[index]?.unitPrice}
                            htmlFor={`${part.id}-unit-price`}
                          >
                            <div className="w-32">
                              <PriceInput
                                price={part.unitPrice}
                                onChange={(unitPrice) =>
                                  updatePart(part.id, { unitPrice: unitPrice ?? null })
                                }
                                min={0}
                                id={`${part.id}-unit-price`}
                              />
                            </div>
                          </Field>

                          <Field label="Price">
                            <div className="text-base leading-4 py-2.5">
                              {MoneyM.format(
                                MoneyM.mult(part.unitPrice || zeroPrice, part.quantity || 1)
                              )}
                            </div>
                          </Field>
                        </div>

                        <div className="flex flex-wrap gap-x-4 justify-between items-center">
                          <div className="flex gap-x-4 gap-y-2 flex-wrap">
                            <Field htmlFor={`${part.id}-in-stock`}>
                              <InStockCheckbox
                                className={classNames(
                                  'whitespace-nowrap',
                                  part.inStock ? 'mt-2' : 'mt-8'
                                )}
                                checked={part.inStock}
                                onChange={(e) => updatePart(part.id, { inStock: e.target.checked })}
                                deliveryMethod={
                                  vendorLink.pickup
                                    ? DeliveryMethod.Pickup
                                    : DeliveryMethod.Shipping
                                }
                              />
                            </Field>

                            {!part.inStock && (
                              <Field
                                label="Available at"
                                htmlFor={`${part.id}-available`}
                                errors={errors.parts[index]?.availableAt}
                              >
                                <DateInput
                                  value={part.availableAt}
                                  onChange={(availableAt) =>
                                    updatePart(part.id, { availableAt: availableAt ?? null })
                                  }
                                  id={`${part.id}-available`}
                                />
                              </Field>
                            )}
                          </div>

                          {form.parts.length > 1 && (
                            <div className="flex grow justify-end">
                              <Action.T
                                className={classNames(
                                  'flex gap-1 items-center whitespace-nowrap',
                                  !part.inStock && 'mt-6'
                                )}
                                onClick={() => removePart(part.id)}
                              >
                                <XCircleIcon className="w-5 h-5" />
                                <span>Remove</span>
                              </Action.T>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className={form.parts.length > 1 ? 'pt-6' : ''}>
                    <Action.T
                      className="flex gap-1 items-center whitespace-nowrap"
                      onClick={() => addPart(buildPart())}
                    >
                      <PlusCircleIcon className="w-5 h-5" />
                      <span>Add part</span>
                    </Action.T>
                  </div>
                </div>

                {vendorLink?.pickup && (
                  <div className="space-y-4 max-w-lg">
                    <h2>Pickup Address</h2>
                    <AddressInput
                      address={form.pickupAddress}
                      onChange={(pickupAddress) => updateForm({ ...form, pickupAddress })}
                      hideFirstLastName
                      hideCompany
                    />
                  </div>
                )}
              </div>
            </Card.Section>
          </Card>
        </Layout.Section>

        <Layout.Section type={windowIsMd ? 'secondary' : 'full'}>
          <Card>
            <Card.Section className="space-y-4">
              <DocumentUpload
                title="Upload Quote"
                uploadContext="buyer_create_quote"
                processing={!!uploadProcessing}
                processingDescription="This may take up to a minute. Quote details will be provided from the information in this file."
                documentUrl={documentUrl}
                documentPreviewUrl={documentPreviewUrl}
                onStarted={uploadStarted}
                onComplete={onDocumentUploadComplete}
                onClear={clearDocument}
              />
            </Card.Section>
          </Card>

          <Card>
            <Card.Section className="space-y-4">
              <Field label="Quote Number (optional)">
                <TextInput
                  value={form.quoteNumber}
                  setValue={(quoteNumber) => updateForm({ quoteNumber })}
                />
              </Field>

              <PricesForm
                subtotal={subtotal}
                shipping={form.shippingCost}
                tax={form.taxCost}
                pickup={vendorLink?.pickup}
                setTax={(taxCost) => updateForm({ taxCost })}
                setShipping={(shippingCost) => updateForm({ shippingCost })}
              />

              <div className="flex justify-end">
                <Action.P type="submit" performing={adding} disabled={!!uploadProcessing}>
                  Create
                </Action.P>
              </div>
            </Card.Section>
          </Card>

          {windowIsMd && <VendorCard vendor={vendor} vertical />}
        </Layout.Section>
      </Layout>
    </Form>
  )
}

export default CreateQuoteForm
